import { useState, useEffect } from "react";
import { Menu, Settings, User, LogOut, Code, Eye, EyeOff, ShieldCheck, Sparkles, Music, ChevronDown } from "lucide-react";
import { navigationCategories, isNavigationItemActive } from "@/lib/navigation-items";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { ThemeToggle } from "./ThemeToggle";
import { UnitSearch } from "./UnitSearch";
import { MusicPlayer } from "./MusicPlayer/MusicPlayer";
import { useExpertMode } from "@/lib/store/expert-mode-store";
import { useSynthwave } from "@/lib/store/synthwave-store";
import { useAuth } from "@/lib/store/auth-store";
import { useMusicPlayer } from "@/lib/store/music-player-store";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { isExpertMode, toggleExpertMode } = useExpertMode();
  const { isSynthwaveEnabled, toggleSynthwave } = useSynthwave();
  const { user, loading, checkAuth, login, logout } = useAuth();
  const { setExpanded: setMusicPlayerExpanded } = useMusicPlayer();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [musicPlayerSheetOpen, setMusicPlayerSheetOpen] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!popoverOpen) {
      // Reset email visibility when popover closes
      setEmailRevealed(false);
    }
  }, [popoverOpen]);

  // Close Sheet when screen size becomes desktop (xl and up)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) {
        if (musicPlayerSheetOpen) {
          setMusicPlayerSheetOpen(false);
          setMusicPlayerExpanded(false);
        }
        // Close drawer when switching to desktop
        if (drawerOpen) {
          setDrawerOpen(false);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    // Check on mount too
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [musicPlayerSheetOpen, drawerOpen]);

  const adminNavigationItems = [
    { icon: ShieldCheck, label: "Verified Users", path: "/deck-library?admin=verified-users" },
    { icon: Settings, label: "Versions", path: "/versions" },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogin = (provider: "discord" | "dev") => {
    login(provider);
  };

  const handleLogout = async () => {
    setPopoverOpen(false);
    await logout();
  };

  const isDevelopment = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const obfuscateEmail = (email: string): string => {
    const [localPart, domain] = email.split("@");
    if (!domain || !localPart) return email;
    
    const obfuscatedLocal = localPart.length > 1 
      ? `${localPart[0]}${"*".repeat(Math.min(localPart.length - 1, 3))}`
      : localPart;
    
    const [domainName, ...tldParts] = domain.split(".");
    const tld = tldParts.join(".");
    if (!domainName) return email;
    
    const obfuscatedDomain = domainName.length > 1
      ? `${domainName[0]}${"*".repeat(Math.min(domainName.length - 1, 3))}`
      : domainName;
    
    return `${obfuscatedLocal}@${obfuscatedDomain}${tld ? `.${tld}` : ""}`;
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full max-w-full border-b border-border bg-background/95 backdrop-blur-sm overflow-hidden">
        <div className="mx-auto grid grid-cols-[auto_1fr] items-center px-2 sm:px-4 py-3 min-w-0 gap-2">
          {/* Left side: Menu and Logo */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* Drawer menu button - hidden on desktop (xl+) */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="xl:hidden text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label="Open navigation menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            <Link to="/" className="text-lg font-bold text-foreground flex-shrink-0">
              <div className="bg-gray-700 dark:bg-transparent px-2 py-1 rounded-xl">
                <img src="/images/branding/waryes.png" alt="Logo" className="h-4 sm:h-6 w-auto" />
              </div>
            </Link>
            {/* Desktop Navigation - visible on xl and above */}
            <nav className="hidden xl:flex items-center gap-1 ml-4">
              {navigationCategories.map((category) => {
                const hasActiveItem = category.items.some((item) =>
                  isNavigationItemActive(location.pathname, item)
                );
                const isOpen = openDropdown === category.title;
                return (
                  <DropdownMenu 
                    key={category.title}
                    open={isOpen} 
                    onOpenChange={(open) => setOpenDropdown(open ? category.title : null)}
                    modal={false}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant={hasActiveItem ? "secondary" : "ghost"}
                        className="text-sm font-medium cursor-pointer"
                        onMouseEnter={() => setOpenDropdown(category.title)}
                        onMouseLeave={() => setOpenDropdown(null)}
                      >
                        {category.title}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="start" 
                      className="w-56" 
                      sideOffset={-8}
                      onMouseEnter={() => setOpenDropdown(category.title)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                        {category.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = isNavigationItemActive(location.pathname, item);
                          return (
                            <DropdownMenuItem
                              key={item.path}
                              onClick={() => handleNavigation(item.path)}
                              className={`cursor-pointer flex items-center justify-between gap-2 ${isActive ? "bg-accent" : ""}`}
                            >
                              <div className="flex items-center min-w-0">
                                <Icon className="mr-2 h-4 w-4 shrink-0" />
                                <span className={isActive ? "font-semibold" : ""}>
                                  {item.label}
                                </span>
                              </div>
                              {item.beta && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-semibold shrink-0">
                                  Beta
                                </Badge>
                              )}
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                );
              })}
              {/* Admin items in navbar if user is admin */}
              {user?.isAdmin && (
                <>
                  <div className="h-6 w-px bg-border mx-1" />
                  <DropdownMenu 
                    open={openDropdown === "admin"} 
                    onOpenChange={(open) => setOpenDropdown(open ? "admin" : null)}
                    modal={false}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="text-sm font-medium cursor-pointer"
                        onMouseEnter={() => setOpenDropdown("admin")}
                        onMouseLeave={() => setOpenDropdown(null)}
                      >
                        Admin
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="start" 
                      className="w-56" 
                      sideOffset={-8}
                      onMouseEnter={() => setOpenDropdown("admin")}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                        {adminNavigationItems.map((item) => {
                          const Icon = item.icon;
                          const isActive =
                            location.pathname === item.path.split("?")[0] &&
                            location.search.includes("admin=verified-users");
                          return (
                            <DropdownMenuItem
                              key={item.path}
                              onClick={() => handleNavigation(item.path)}
                              className={`cursor-pointer ${isActive ? "bg-accent" : ""}`}
                            >
                              <Icon className="mr-2 h-4 w-4" />
                              <span className={isActive ? "font-semibold" : ""}>
                                {item.label}
                              </span>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                </>
              )}
            </nav>
          </div>

          {/* Right side: Search and Controls */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 justify-end">
            {/* Unit Search */}
            <div className="w-full max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] min-w-0">
              <UnitSearch />
            </div>

            {/* Music Player - hidden on small/tablet screens, shown on lg and up (desktop only), also hidden when Sheet is open */}
            {!musicPlayerSheetOpen && (
              <div className="hidden lg:block">
                <MusicPlayer />
              </div>
            )}

            {/* Unified Popover for Expert Mode, Theme, and Login */}
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground p-0 h-8 w-8 rounded-full relative"
                  aria-label="More options"
                >
                  {loading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                  ) : user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || "User"}
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                  <Badge
                    variant="secondary"
                    className="absolute -bottom-0.5 -right-0.5 text-[8px] px-0.5 py-0 h-3 leading-none pointer-events-none"
                  >
                    {isExpertMode ? "Expert" : "Simple"}
                  </Badge>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-0">
                <div className="flex flex-col">
                  {/* User Profile Section */}
                  {loading ? (
                    <div className="flex items-center justify-center p-4">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                    </div>
                  ) : user ? (
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name || "User"}
                          className="h-10 w-10 rounded-full flex-shrink-0"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                        {user.email && (
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                              {emailRevealed ? user.email : obfuscateEmail(user.email)}
                            </p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEmailRevealed(!emailRevealed);
                              }}
                              className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0.5"
                              aria-label={emailRevealed ? "Hide email" : "Reveal email"}
                              type="button"
                            >
                              {emailRevealed ? (
                                <EyeOff className="h-3.5 w-3.5" />
                              ) : (
                                <Eye className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Settings Section */}
                  <div className="p-2">
                    {/* Music Player - visible on small/tablet screens, hidden on desktop (lg+) */}
                    <div className="lg:hidden">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setMusicPlayerExpanded(true);
                          setMusicPlayerSheetOpen(true);
                          setPopoverOpen(false);
                        }}
                        className="w-full justify-start text-muted-foreground hover:text-foreground"
                      >
                        <Music className="h-4 w-4 mr-2" />
                        Music Player
                      </Button>
                    </div>

                    {/* Expert Mode Toggle */}
                    <div className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-accent/50 transition-colors">
                      <span className="text-sm font-medium">Expert Mode</span>
                      <Switch checked={isExpertMode} onCheckedChange={toggleExpertMode} />
                    </div>
                    
                    {/* Theme Toggle */}
                    <div className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-accent/50 transition-colors">
                      <span className="text-sm font-medium">Theme</span>
                      <ThemeToggle />
                    </div>

                    {/* Synthwave Background Toggle */}
                    <div className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-accent/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Synthwave Background</span>
                      </div>
                      <Switch checked={isSynthwaveEnabled} onCheckedChange={toggleSynthwave} />
                    </div>
                  </div>

                  {/* Divider */}
                  {user && <div className="h-px bg-border" />}

                  {/* Account Section */}
                  {user && (
                    <div className="p-2">
                      <Link
                        to="/account-settings"
                        onClick={() => setPopoverOpen(false)}
                        className="block"
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-muted-foreground hover:text-foreground"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Account Settings
                        </Button>
                      </Link>
                    </div>
                  )}

                  {/* Divider */}
                  {user && <div className="h-px bg-border" />}

                  {/* Login/Logout Section */}
                  <div className="p-2">
                    {user ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogout}
                        className="w-full justify-start text-muted-foreground hover:text-foreground"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </Button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {isDevelopment && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLogin("dev")}
                            className="w-full justify-start border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-950"
                          >
                            <Code className="h-4 w-4 mr-2" />
                            Dev Login
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLogin("discord")}
                          className="w-full justify-start"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Login with Discord
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[300px] sm:w-[400px] p-0 flex flex-col overflow-hidden">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/40 flex-shrink-0">
            <SheetTitle className="text-xl font-semibold tracking-tight">Navigation</SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col gap-0.5 p-4 overflow-y-auto flex-1">
            {navigationCategories.map((category, categoryIndex) => (
              <div key={category.title}>
                {/* Category header */}
                <div className="px-4 py-2 mt-2 first:mt-0">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {category.title}
                  </span>
                </div>
                {/* Category items */}
                {category.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = isNavigationItemActive(location.pathname, item);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`
                        group relative flex items-center gap-4 px-4 py-1 text-left rounded-xl
                        transition-all duration-200 cursor-pointer w-full
                        ${isActive 
                          ? 'bg-primary/10 text-primary shadow-sm border-l-4 border-primary' 
                          : 'text-foreground hover:bg-accent/50 hover:text-foreground'
                        }
                      `}
                      type="button"
                    >
                      <div className={`
                        flex items-center justify-center w-10 h-10 rounded-lg
                        transition-all duration-200
                        ${isActive 
                          ? 'bg-primary/20 text-primary shadow-sm' 
                          : 'bg-muted/50 text-muted-foreground group-hover:bg-accent group-hover:text-foreground'
                        }
                      `}>
                        <Icon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                      </div>
                      <span className={`flex flex-1 items-center gap-2 min-w-0 font-medium text-base transition-colors duration-200 ${isActive ? 'text-primary' : ''}`}>
                        <span className="truncate">{item.label}</span>
                        {item.beta && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-semibold shrink-0">
                            Beta
                          </Badge>
                        )}
                      </span>
                      {isActive && (
                        <div className="absolute right-4 w-2 h-2 rounded-full bg-primary animate-pulse" />
                      )}
                    </button>
                  );
                })}
                {/* Add separator between categories except after the last one */}
                {categoryIndex < navigationCategories.length - 1 && (
                  <div className="h-px bg-border my-2 mx-4" />
                )}
              </div>
            ))}
            {user?.isAdmin && (
              <>
                <div className="h-px bg-border my-2" />
                <div className="px-4 py-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin</span>
                </div>
                {adminNavigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path.split('?')[0] && 
                                   location.search.includes('admin=verified-users');
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`
                        group relative flex items-center gap-4 px-4 py-1 text-left rounded-xl
                        transition-all duration-200 cursor-pointer w-full
                        ${isActive 
                          ? 'bg-primary/10 text-primary shadow-sm border-l-4 border-primary' 
                          : 'text-foreground hover:bg-accent/50 hover:text-foreground'
                        }
                      `}
                      type="button"
                    >
                      <div className={`
                        flex items-center justify-center w-10 h-10 rounded-lg
                        transition-all duration-200
                        ${isActive 
                          ? 'bg-primary/20 text-primary shadow-sm' 
                          : 'bg-muted/50 text-muted-foreground group-hover:bg-accent group-hover:text-foreground'
                        }
                      `}>
                        <Icon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                      </div>
                      <span className={`font-medium text-base transition-colors duration-200 ${isActive ? 'text-primary' : ''}`}>
                        {item.label}
                      </span>
                      {isActive && (
                        <div className="absolute right-4 w-2 h-2 rounded-full bg-primary animate-pulse" />
                      )}
                    </button>
                  );
                })}
              </>
            )}
          </nav>
        </SheetContent>
      </Sheet>

      {/* Music Player Sheet - for mobile screens */}
      <Sheet 
        open={musicPlayerSheetOpen} 
        onOpenChange={(open) => {
          setMusicPlayerSheetOpen(open);
          if (!open) {
            // Collapse the music player when Sheet closes
            setMusicPlayerExpanded(false);
          }
        }}
      >
        <SheetContent side="bottom" className="h-[80vh] p-0 flex flex-col overflow-hidden">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/40 flex-shrink-0">
            <SheetTitle className="text-xl font-semibold tracking-tight">Music Player</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6">
            <MusicPlayer isInSheet={true} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

